import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Star, Phone, MessageCircle, ChevronRight, Search, Filter, ArrowLeft, Store, Loader2 } from 'lucide-react';
import SEOHead, { buildCategoryTitle, buildCategoryDescription, buildBreadcrumbJsonLd } from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import BusinessCard from '../components/BusinessCard';

const API = process.env.REACT_APP_BACKEND_URL;

function slugify(text) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[-\s]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function CategoriaCidade() {
  const { municipioSlug, categoriaSlug, subcategoriaSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        let url = `${API}/api/v2/seo/category-meta?municipio_slug=${municipioSlug}&categoria_slug=${categoriaSlug}`;
        if (subcategoriaSlug) url += `&subcategoria_slug=${subcategoriaSlug}`;
        const [res, relRes] = await Promise.all([
          fetch(url),
          fetch(`${API}/api/v2/seo/related?municipio_slug=${municipioSlug}&categoria_slug=${categoriaSlug}`),
        ]);
        const json = await res.json();
        if (json.error) {
          setError(json.error);
        } else {
          setData(json);
        }
        if (relRes.ok) setRelated(await relRes.json());
      } catch {
        setError('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [municipioSlug, categoriaSlug, subcategoriaSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4 px-4">
        <Store className="w-16 h-16 text-gray-300" />
        <h1 className="text-xl font-bold text-gray-700">Página não encontrada</h1>
        <p className="text-gray-500 text-center">
          A categoria ou município solicitado não foi encontrado.
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-2 px-6 py-2.5 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          data-testid="seo-back-home-btn"
        >
          Voltar ao início
        </button>
      </div>
    );
  }

  const { municipio, categoria, subcategoria, total, businesses } = data;
  const uf = municipio.uf || 'MG';
  const pageTitle = buildCategoryTitle(categoria.nome, subcategoria, municipio.nome, uf);
  const pageDesc = buildCategoryDescription(categoria.nome, subcategoria, municipio.nome, uf, total);
  const canonicalPath = subcategoriaSlug
    ? `/mg/${municipioSlug}/${categoriaSlug}/${subcategoriaSlug}`
    : `/mg/${municipioSlug}/${categoriaSlug}`;

  // Breadcrumb items for JSON-LD
  const breadcrumbItems = [
    { name: 'Início', url: '/' },
    { name: municipio.nome, url: `/${municipio.slug}` },
    { name: categoria.nome, url: `/mg/${municipioSlug}/${categoriaSlug}` },
  ];
  if (subcategoria) {
    breadcrumbItems.push({ name: subcategoria, url: canonicalPath });
  }

  const jsonLd = [buildBreadcrumbJsonLd(breadcrumbItems)];

  // Subcategories to show when on a category page (not subcategory)
  const subcategoriasLinks = !subcategoria ? (categoria.subcategorias || []) : [];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonical={canonicalPath}
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-gray-50" data-testid="categoria-cidade-page">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[var(--primary)] to-[#2d004d] text-white">
          <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
            <Breadcrumbs
              items={subcategoria
                ? [
                    { label: categoria.nome, path: `/mg/${municipioSlug}/${categoriaSlug}` },
                    { label: subcategoria }
                  ]
                : [{ label: categoria.nome }]
              }
              municipio={municipio}
            />

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-4" data-testid="seo-page-h1">
              {subcategoria || categoria.nome}
            </h1>
            <p className="text-white/80 mt-2 text-sm sm:text-base flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              {municipio.nome}, {uf}
              {total > 0 && <span className="ml-2">· {total} {total === 1 ? 'negócio encontrado' : 'negócios encontrados'}</span>}
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
          {/* Subcategory links (only on category page) */}
          {subcategoriasLinks.length > 0 && (
            <div className="mb-8" data-testid="subcategory-links">
              <h2 className="text-base sm:text-lg font-semibold mb-3 text-gray-800">
                Subcategorias de {categoria.nome}
              </h2>
              <div className="flex flex-wrap gap-2">
                {subcategoriasLinks.map(sub => {
                  const subSlug = slugify(sub);
                  return (
                    <Link
                      key={sub}
                      to={`/mg/${municipioSlug}/${categoriaSlug}/${subSlug}`}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                      data-testid={`subcat-link-${subSlug}`}
                    >
                      {sub}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Business listing */}
          {businesses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="seo-business-grid">
              {businesses.map(biz => (
                <BusinessCard
                  key={biz.id}
                  business={biz}
                  municipioSlug={municipioSlug}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                Nenhum negócio encontrado nesta categoria em {municipio.nome}.
              </p>
              <Link
                to={`/${municipioSlug}/explorar`}
                className="mt-4 inline-block text-[var(--primary)] font-medium hover:underline"
              >
                Explorar todos os negócios
              </Link>
            </div>
          )}

          {/* Internal linking: related categories in this city */}
          {related?.related_categories?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200" data-testid="related-categories">
              <h2 className="text-base sm:text-lg font-semibold mb-3 text-gray-800">
                Outras categorias em {municipio.nome}
              </h2>
              <div className="flex flex-wrap gap-2">
                {related.related_categories.map(cat => (
                  <Link
                    key={cat.id}
                    to={cat.url}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                    data-testid={`related-cat-${cat.id}`}
                  >
                    {cat.icone && <span>{cat.icone}</span>}
                    {cat.nome}
                    <span className="text-xs text-gray-400">({cat.count})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Neighborhood context */}
          {related?.neighborhoods?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-100" data-testid="neighborhoods-list">
              <h2 className="text-base sm:text-lg font-semibold mb-3 text-gray-800">
                Bairros com negócios em {municipio.nome}
              </h2>
              <div className="flex flex-wrap gap-2">
                {related.neighborhoods.map(n => (
                  <span key={n.nome} className="px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-full text-sm text-gray-600">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {n.nome} ({n.count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Explore all link */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <Link
              to={`/${municipioSlug}/explorar`}
              className="inline-flex items-center gap-1.5 text-[var(--primary)] font-medium hover:underline"
              data-testid="explore-all-link"
            >
              Ver todos os negócios em {municipio.nome}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
